import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Globe } from 'lucide-react';
import { usePageNavigation } from '../../lib/contexts/PageNavigationContext';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import DarkModeToggle from '../ui/DarkModeToggle';
import 'flag-icons/css/flag-icons.min.css';

const MobileMenuOverlay = () => {
  const { isCardNavOpen, setIsCardNavOpen } = usePageNavigation();
  const { t, language, setLanguage, isTransitioning } = useLanguage();
  const navigate = useNavigate();
  const [showLanguageScreen, setShowLanguageScreen] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  // Close overlay when clicking backdrop
  const closeOverlay = () => {
    setIsCardNavOpen(false);
    setShowLanguageScreen(false);
  };

  // Handle navigation and close overlay
  const handleNavigation = (path: string) => {
    setIsCardNavOpen(false);
    setShowLanguageScreen(false);
    navigate(path);
  };

  // Handle language selection
  const handleLanguageSelect = (langCode: 'en' | 'is') => {
    if (langCode === language) {
      setShowLanguageScreen(false);
      return;
    }

    setIsChangingLanguage(true);

    setTimeout(() => {
      setLanguage(langCode, true);
      setTimeout(() => {
        setIsChangingLanguage(false);
        setShowLanguageScreen(false);
      }, 200);
    }, 50);
  };

  // Show language selection screen
  const showLanguageSelection = () => {
    setShowLanguageScreen(true);
  };

  // Go back to main menu
  const goBackToMenu = () => {
    setShowLanguageScreen(false);
  };

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isCardNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setShowLanguageScreen(false);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isCardNavOpen]);

  const navItems = [
    { label: t.common.nav.home || 'Home', path: '/' },
    { label: t.common.nav.about, path: '/about' },
    { label: t.common.nav.menu, path: '/menu' },
    { label: t.common.nav.wineList, path: '/wine-list' },
    { label: t.common.nav.contact, path: '/contact' },
    { label: t.common.nav.careers || 'Careers', path: '/careers' },
  ];

  const languages = {
    en: { name: 'English', flag: 'gb' },
    is: { name: 'Íslenska', flag: 'is' },
  };

  return (
    <AnimatePresence>
      {isCardNavOpen && (
        <div className="lg:hidden">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={closeOverlay}
          />

          {/* Menu Panel */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 300,
              duration: 0.4,
            }}
            className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-3xl shadow-2xl h-[85vh] overflow-hidden flex flex-col"
          >
            {/* Grabber Handle */}
            <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
              <button
                onClick={closeOverlay}
                className="w-12 h-1 bg-neutral-300 rounded-full hover:bg-neutral-400 transition-colors"
                aria-label="Close menu"
              />
            </div>

            {/* Menu Content */}
            <div className="px-6 pb-6 overflow-y-auto flex-1 mobile-bottom-dock">
              {!showLanguageScreen ? (
                <>
                  {/* Main Menu Header */}
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-serif font-semibold text-neutral-900">Menu</h2>
                    <div className="flex items-center gap-3">
                      <DarkModeToggle size="md" />
                    </div>
                  </div>

                  {/* Navigation Links */}
                  <div className="space-y-1 mb-6">
                    {navItems.map((item, index) => (
                      <motion.button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center justify-between px-4 py-4 text-left hover:bg-neutral-50 rounded-xl transition-colors min-h-[44px]"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                      >
                        <span className="text-neutral-900 font-medium text-base">{item.label}</span>
                        <svg
                          className="w-5 h-5 text-neutral-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </motion.button>
                    ))}

                    {/* Language Selection Button */}
                    <motion.button
                      onClick={showLanguageSelection}
                      className="w-full flex items-center justify-between px-4 py-4 text-left hover:bg-neutral-50 rounded-xl transition-colors min-h-[44px]"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: navItems.length * 0.05, duration: 0.3 }}
                      disabled={isChangingLanguage || isTransitioning}
                    >
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-neutral-500" />
                        <span className="text-neutral-900 font-medium text-base">Language</span>
                        <span className="text-sm text-neutral-500">
                          ({languages[language].name})
                        </span>
                      </div>
                      <svg
                        className="w-5 h-5 text-neutral-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </motion.button>
                  </div>

                  {/* Book a Table Button */}
                  <motion.button
                    onClick={() => handleNavigation('/reservations')}
                    className="w-full bg-red-800 text-white py-4 rounded-2xl font-semibold text-base hover:bg-red-700 transition-colors min-h-[44px]"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                  >
                    {t.common.nav.reservations}
                  </motion.button>

                  {/* Restaurant Info */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    className="mt-6 pt-4 border-t border-neutral-200 text-center"
                  >
                    <p className="text-sm text-neutral-600 mb-1">Rossopomodoro</p>
                    <p className="text-xs text-neutral-500">Laugavegi 40a, 101 Reykjavík</p>
                    <p className="text-xs text-neutral-500">+354 561 0500</p>
                  </motion.div>
                </>
              ) : (
                <>
                  {/* Language Selection Screen */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={goBackToMenu}
                        className="p-2 text-neutral-600 hover:text-neutral-800 transition-colors rounded-full hover:bg-neutral-100"
                        aria-label="Back to menu"
                        disabled={isChangingLanguage || isTransitioning}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <h2 className="text-xl font-serif font-semibold text-neutral-900">
                        Language
                      </h2>
                    </div>
                  </div>

                  {/* Language Options */}
                  <div className="space-y-3 mb-6">
                    {Object.entries(languages).map(([code, { name, flag }], index) => (
                      <motion.button
                        key={code}
                        onClick={() => handleLanguageSelect(code as 'en' | 'is')}
                        className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl transition-all min-h-[60px] ${
                          language === code
                            ? 'bg-red-50 border-2 border-red-200 text-red-800'
                            : 'bg-neutral-50 border-2 border-transparent hover:bg-neutral-100 text-neutral-700'
                        } ${isChangingLanguage || isTransitioning ? 'pointer-events-none opacity-50' : ''}`}
                        disabled={isChangingLanguage || isTransitioning}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.3 }}
                      >
                        <motion.div
                          className="relative w-10 h-10 rounded-full overflow-hidden border border-gray-200 shadow-sm bg-white flex items-center justify-center"
                          animate={{
                            scale: language === code && isChangingLanguage ? 1.1 : 1,
                            rotate: language === code && isChangingLanguage ? 360 : 0,
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          <span
                            className={`fi fi-${flag} fis`}
                            style={{
                              display: 'block',
                              width: '100%',
                              height: '100%',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              lineHeight: 0,
                            }}
                          />
                        </motion.div>
                        <div className="flex-1 text-left">
                          <span className="text-lg font-medium">{name}</span>
                          {language === code && (
                            <div className="flex items-center gap-1 mt-1">
                              <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                              <span className="text-sm text-red-600">Current</span>
                            </div>
                          )}
                        </div>
                        {language === code && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-red-600"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </motion.div>
                        )}
                      </motion.button>
                    ))}
                  </div>

                  {/* Loading State */}
                  {(isChangingLanguage || isTransitioning) && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-4"
                    >
                      <div className="inline-flex items-center gap-2 text-blue-600">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        >
                          <Globe className="w-5 h-5" />
                        </motion.div>
                        <span className="text-sm font-medium">Changing language...</span>
                      </div>
                    </motion.div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default MobileMenuOverlay;
